import type { ReactNode } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  styled,
} from '@mui/material';

export interface BaseDialogProps {
  actionsComponent?: ReactNode;
  onClose?: () => void;
  title?: string | ReactNode;
  open?: boolean;
  children: ReactNode;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  icon?: ReactNode;
}

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[500],
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
}));

const ActionsContainer = styled(DialogActions)(({ theme }) => ({
  margin: theme.spacing(2),
  display: 'flex',
  flexDirection: theme.breakpoints.up('md') ? 'row' : 'column',
  justifyContent: 'flex-end',
  gap: theme.spacing(1),
  flex: 1,
  '& > *': {
    width: theme.breakpoints.up('md') ? 'auto' : '100%',
  },
}));

export const BaseDialog = ({
  actionsComponent,
  onClose,
  title,
  children,
  open = true,
  fullWidth = true,
  maxWidth = 'sm',
  icon,
}: BaseDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.();
        }
      }}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      disableEscapeKeyDown={false}
    >
      <CloseButton aria-label="fechar" onClick={onClose}>
        <CloseIcon />
      </CloseButton>
      {icon && (
        <Stack px={3} mt={6}>
          {icon}
        </Stack>
      )}
      {title && <DialogTitle>{title}</DialogTitle>}
      {children && <DialogContent>{children}</DialogContent>}
      {actionsComponent && (
        <ActionsContainer>{actionsComponent}</ActionsContainer>
      )}
    </Dialog>
  );
};
